// P3.2 — `driftless project apply <id> --file @plan.json [--dry-run] [--json]`:
// declaratively apply a whole project plan (goal + many cards + deps) idempotently.
// No more one-off API scripts for large plans. Reruns are no-ops; unmentioned cards
// are preserved; deletes never happen by default. The pure planner here is fully
// unit-tested; the orchestrator takes an injected API client so the create/update/
// no-op/dependency flow is testable without a live backend.

import { CARD_STATUSES, PROJECT_STATUSES } from './project'

export interface PlanCard {
  // identity (any one): a raw card id, a stable plan-local key (for dep refs), and
  // the title (the default natural key matched against existing cards).
  id?: string
  key?: string
  title: string
  description?: string
  acceptance?: string
  validate?: string
  status?: string
  owner?: string
  depends_on?: string[] // refs by id | key | title (existing OR same-plan cards)
}

export interface ProjectPlan {
  title?: string
  goal?: string
  status?: string
  playbook?: string
  cards: PlanCard[]
}

// What the cards-list endpoint returns (index: id/title/status/owner) plus the
// detail fields we compare after a per-card GET.
export interface ExistingCard {
  id: string
  title: string
  status?: string
  owner?: string | null
  description?: string | null
  acceptance?: string | null
  validate?: string | null
  depends_on?: string[]
}

export type CardAction = 'create' | 'update' | 'unchanged'
export interface CardDecision {
  plan: PlanCard
  action: CardAction
  matchId: string | null // existing card id when matched
}

export type DepResolution =
  | { ref: string; kind: 'existing'; id: string }
  | { ref: string; kind: 'plan'; key: string } // resolves to a same-plan card (by key/title)
  | { ref: string; kind: 'unresolved' }

// ── Plan parsing ───────────────────────────────────────────────────────────

export function normalizeProjectPlan(parsed: unknown): ProjectPlan {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw 'plan must be a JSON object { goal?, cards: [...] }'
  }
  const p = parsed as Record<string, unknown>
  const rawCards = p['cards']
  if (!Array.isArray(rawCards) || rawCards.length === 0) throw 'plan must contain a non-empty "cards" array'
  if (p['status'] !== undefined && !PROJECT_STATUSES.includes(p['status'] as string)) {
    throw `plan project status "${String(p['status'])}" is invalid`
  }
  const seenKeys = new Set<string>()
  const cards = rawCards.map((raw, i): PlanCard => {
    if (!raw || typeof raw !== 'object') throw `card #${i + 1} is not an object`
    const c = raw as Record<string, unknown>
    if (typeof c['title'] !== 'string' || !c['title'].trim()) throw `card #${i + 1} is missing a title`
    if (c['status'] !== undefined && !CARD_STATUSES.includes(c['status'] as string)) throw `card #${i + 1} has invalid status "${String(c['status'])}"`
    if (c['depends_on'] !== undefined && !Array.isArray(c['depends_on'])) throw `card #${i + 1} depends_on must be an array`
    const key = typeof c['key'] === 'string' ? c['key'] : undefined
    if (key) {
      if (seenKeys.has(key)) throw `duplicate card key "${key}"`
      seenKeys.add(key)
    }
    return {
      id: typeof c['id'] === 'string' ? c['id'] : undefined,
      key,
      title: c['title'],
      description: typeof c['description'] === 'string' ? c['description'] : undefined,
      acceptance: typeof c['acceptance'] === 'string' ? c['acceptance'] : undefined,
      validate: typeof c['validate'] === 'string' ? c['validate'] : undefined,
      status: typeof c['status'] === 'string' ? c['status'] : undefined,
      owner: typeof c['owner'] === 'string' ? c['owner'] : undefined,
      depends_on: Array.isArray(c['depends_on']) ? (c['depends_on'] as unknown[]).map(String) : undefined,
    }
  })
  return {
    title: typeof p['title'] === 'string' ? p['title'] : undefined,
    goal: typeof p['goal'] === 'string' ? p['goal'] : undefined,
    status: typeof p['status'] === 'string' ? p['status'] : undefined,
    playbook: typeof p['playbook'] === 'string' ? p['playbook'] : undefined,
    cards,
  }
}

// ── Matching + dependency resolution (pure) ─────────────────────────────────

// Match a plan card to an existing one: by id first, then by exact title. Returns
// the existing card id or null (→ create). Title is the default stable key, so a
// rerun matches and updates rather than duplicating.
export function matchPlanCard(card: PlanCard, byId: Map<string, ExistingCard>, byTitle: Map<string, ExistingCard>): ExistingCard | null {
  if (card.id && byId.has(card.id)) return byId.get(card.id)!
  const t = byTitle.get(card.title)
  return t ?? null
}

// Resolve a depends_on ref against existing cards and the same-plan cards. Order:
// existing id → existing title → same-plan key → same-plan title → unresolved.
export function resolveDep(
  ref: string,
  byId: Map<string, ExistingCard>,
  byTitle: Map<string, ExistingCard>,
  planByKey: Map<string, PlanCard>,
  planByTitle: Map<string, PlanCard>,
): DepResolution {
  if (byId.has(ref)) return { ref, kind: 'existing', id: ref }
  const exTitle = byTitle.get(ref)
  if (exTitle) return { ref, kind: 'existing', id: exTitle.id }
  if (planByKey.has(ref)) return { ref, kind: 'plan', key: planByKey.get(ref)!.key ?? planByKey.get(ref)!.title }
  if (planByTitle.has(ref)) return { ref, kind: 'plan', key: planByTitle.get(ref)!.key ?? ref }
  return { ref, kind: 'unresolved' }
}

// True when a matched existing card already equals the plan card on every settable
// field (so it's a no-op, not an update). depends_on compared as RESOLVED id sets.
export function cardUnchanged(plan: PlanCard, existing: ExistingCard, planDepIds: string[]): boolean {
  const eq = (a: unknown, b: unknown) => (a ?? '') === (b ?? '')
  if (!eq(plan.title, existing.title)) return false
  if (plan.description !== undefined && !eq(plan.description, existing.description)) return false
  if (plan.acceptance !== undefined && !eq(plan.acceptance, existing.acceptance)) return false
  if (plan.validate !== undefined && !eq(plan.validate, existing.validate)) return false
  if (plan.status !== undefined && !eq(plan.status, existing.status)) return false
  if (plan.owner !== undefined && !eq(plan.owner, existing.owner)) return false
  if (plan.depends_on !== undefined) {
    const a = [...planDepIds].sort()
    const b = [...(existing.depends_on ?? [])].sort()
    if (a.length !== b.length || a.some((x, i) => x !== b[i])) return false
  }
  return true
}

// Build the lookup indexes once.
export function indexExisting(existing: ExistingCard[]): { byId: Map<string, ExistingCard>; byTitle: Map<string, ExistingCard> } {
  const byId = new Map<string, ExistingCard>()
  const byTitle = new Map<string, ExistingCard>()
  for (const c of existing) {
    byId.set(c.id, c)
    // First title wins (a duplicate-title workspace is a pre-existing mess; we don't
    // make it worse — match the first deterministically).
    if (!byTitle.has(c.title)) byTitle.set(c.title, c)
  }
  return { byId, byTitle }
}

export function indexPlan(cards: PlanCard[]): { planByKey: Map<string, PlanCard>; planByTitle: Map<string, PlanCard> } {
  const planByKey = new Map<string, PlanCard>()
  const planByTitle = new Map<string, PlanCard>()
  for (const c of cards) {
    if (c.key) planByKey.set(c.key, c)
    if (!planByTitle.has(c.title)) planByTitle.set(c.title, c)
  }
  return { planByKey, planByTitle }
}

// ── Orchestrator ─────────────────────────────────────────────────────────────

export interface ApplyApi {
  get<T = unknown>(path: string): Promise<T>
  post<T = unknown>(path: string, body: unknown): Promise<T>
  patch<T = unknown>(path: string, body: unknown): Promise<T>
}

export interface ApplySummary {
  dry_run: boolean
  project: { changed: boolean; fields: string[] }
  created: Array<{ title: string; id?: string }>
  updated: Array<{ title: string; id: string }>
  unchanged: Array<{ title: string; id: string }>
  skipped: Array<{ title: string; reason: string }>
  warnings: string[]
  dependency_map: Record<string, string[]> // plan card title → resolved dep refs (ids when known)
}

function cardBody(c: PlanCard): Record<string, unknown> {
  const b: Record<string, unknown> = { title: c.title }
  if (c.description !== undefined) b['description'] = c.description
  if (c.acceptance !== undefined) b['acceptance'] = c.acceptance
  if (c.validate !== undefined) b['validate'] = c.validate
  if (c.status !== undefined) b['status'] = c.status
  if (c.owner !== undefined) b['owner_clerk_id'] = c.owner
  return b
}

// Fetch every existing card (the list endpoint is bounded, so page through it).
async function fetchAllCards(api: ApplyApi, cardsBase: string): Promise<ExistingCard[]> {
  const out: ExistingCard[] = []
  const limit = 200
  let offset = 0
  for (let guard = 0; guard < 1000; guard++) {
    const page = (await api.get<unknown[]>(`${cardsBase}?limit=${limit}&offset=${offset}`)) ?? []
    const rows = Array.isArray(page) ? page : []
    out.push(...(rows as ExistingCard[]))
    if (rows.length < limit) break
    offset += limit
  }
  return out
}

export async function runProjectApply(
  api: ApplyApi,
  base: string,
  projectId: string,
  plan: ProjectPlan,
  opts: { dryRun: boolean },
): Promise<ApplySummary> {
  const cardsBase = `${base}/${encodeURIComponent(projectId)}/cards`
  const projectPath = `${base}/${encodeURIComponent(projectId)}`

  // Project field diff (only fields the plan specifies that actually differ).
  const project = (await api.get<Record<string, unknown>>(projectPath)) ?? {}
  const projFields: string[] = []
  const projBody: Record<string, unknown> = {}
  if (plan.title !== undefined && plan.title !== project['title']) { projBody['title'] = plan.title; projFields.push('title') }
  if (plan.goal !== undefined && plan.goal !== project['goal']) { projBody['goal'] = plan.goal; projFields.push('goal') }
  if (plan.status !== undefined && plan.status !== project['status']) { projBody['status'] = plan.status; projFields.push('status') }
  if (plan.playbook !== undefined && plan.playbook !== project['playbook_slug']) { projBody['playbook_slug'] = plan.playbook; projFields.push('playbook') }

  const existing = await fetchAllCards(api, cardsBase)
  const { byId, byTitle } = indexExisting(existing)
  const { planByKey, planByTitle } = indexPlan(plan.cards)

  const summary: ApplySummary = {
    dry_run: opts.dryRun,
    project: { changed: projFields.length > 0, fields: projFields },
    created: [], updated: [], unchanged: [], skipped: [], warnings: [], dependency_map: {},
  }

  // Resolve dependency refs for each card (for the dependency_map + warnings). A ref
  // that resolves to a same-plan card carries the plan key; its real id is filled at
  // execution. Unresolved refs are warned and dropped.
  const resolvedDeps = new Map<PlanCard, DepResolution[]>()
  for (const c of plan.cards) {
    const res = (c.depends_on ?? []).map((ref) => resolveDep(ref, byId, byTitle, planByKey, planByTitle))
    resolvedDeps.set(c, res)
    summary.dependency_map[c.title] = res.map((r) => (r.kind === 'existing' ? r.id : r.kind === 'plan' ? `plan:${r.key}` : `unresolved:${r.ref}`))
    for (const r of res) if (r.kind === 'unresolved') summary.warnings.push(`card "${c.title}": depends_on "${r.ref}" resolves to no existing or planned card — dropped`)
  }

  // Per-card decision. A matched card is compared field-by-field (it needs a detail
  // GET — the list index lacks description/acceptance/validate/depends_on).
  const decisions: CardDecision[] = []
  for (const c of plan.cards) {
    const match = matchPlanCard(c, byId, byTitle)
    if (!match) { decisions.push({ plan: c, action: 'create', matchId: null }); continue }
    const detail = (await api.get<ExistingCard>(`${cardsBase}/${encodeURIComponent(match.id)}`)) ?? match
    const planDepIds = (resolvedDeps.get(c) ?? []).filter((r) => r.kind === 'existing').map((r) => (r as { id: string }).id)
    const allDepsExisting = (resolvedDeps.get(c) ?? []).every((r) => r.kind === 'existing')
    const unchanged = allDepsExisting && cardUnchanged(c, detail, planDepIds)
    decisions.push({ plan: c, action: unchanged ? 'unchanged' : 'update', matchId: match.id })
  }

  // Warn about existing cards the plan does NOT mention (preserved, never deleted).
  const mentioned = new Set(decisions.filter((d) => d.matchId).map((d) => d.matchId))
  for (const e of existing) if (!mentioned.has(e.id)) summary.warnings.push(`existing card "${e.title}" (${e.id}) is not in the plan — preserved (no prune)`)

  if (opts.dryRun) {
    for (const d of decisions) {
      if (d.action === 'create') summary.created.push({ title: d.plan.title })
      else if (d.action === 'update') summary.updated.push({ title: d.plan.title, id: d.matchId! })
      else summary.unchanged.push({ title: d.plan.title, id: d.matchId! })
    }
    return summary
  }

  // ── Execute. Pass 1: project + create/update fields (no deps yet) → id map.
  if (projFields.length > 0) await api.patch(projectPath, projBody)
  const idFor = new Map<string, string>() // key|title → resulting card id
  for (const d of decisions) {
    let id = d.matchId
    if (d.action === 'create') {
      const res = (await api.post<{ id: string }>(cardsBase, cardBody(d.plan)))
      id = res.id
      summary.created.push({ title: d.plan.title, id })
    } else if (d.action === 'update') {
      await api.patch(`${cardsBase}/${encodeURIComponent(d.matchId!)}`, cardBody(d.plan))
      summary.updated.push({ title: d.plan.title, id: d.matchId! })
    } else {
      summary.unchanged.push({ title: d.plan.title, id: d.matchId! })
    }
    if (id) {
      if (d.plan.key) idFor.set(d.plan.key, id)
      idFor.set(d.plan.title, id)
    }
  }

  // ── Pass 2: depends_on, now that every plan card has an id. Resolve each ref to a
  // concrete id (existing id, or a same-plan card's freshly-created id); skip + warn
  // the unresolved. Only PATCH when the card actually has deps.
  for (const d of decisions) {
    const res = resolvedDeps.get(d.plan) ?? []
    if (res.length === 0) continue
    const ids: string[] = []
    for (const r of res) {
      if (r.kind === 'existing') ids.push(r.id)
      else if (r.kind === 'plan') { const id = idFor.get(r.key) ?? idFor.get(r.ref); if (id) ids.push(id) }
    }
    const selfId = d.matchId ?? idFor.get(d.plan.key ?? d.plan.title)
    if (selfId && ids.length > 0) await api.patch(`${cardsBase}/${encodeURIComponent(selfId)}`, { depends_on: ids })
  }

  return summary
}
